import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { RouterState } from 'connected-react-router'
import moment from 'moment'
import { type SelfDescribingJson, setUserId } from '@snowplow/browser-tracker'

import { AppStore } from '../../store'
import config from 'constants/config'
import { CART_SCHEMA, PAGE_VIEW_CONTEXT_SCHEMA } from 'constants/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { SearchEventData } from 'contexts/GlobalSearch/GlobalSearchTracking'
import {
  buildCartContext,
  buildInsuranceUpsellContext,
  buildSearchContext,
  cartEventsMap,
  createOfferItemContexts,
  createTourOfferItemContexts,
  experienceOfferEventsMap,
  frontPageEventsMap,
  getExperienceContext,
  getItemsContextForOffer,
  getItemsContextForCheckout,
  getOfferContext,
  mapToSnowplowFilters,
  offerEventsMap,
  getPageOfferCategory,
  getItemsContextForOrder,
} from './helpers'
import type {
  CartExtras,
  CartTrackingEvent,
  ExperienceEventDefinitionKey,
  ExperienceEventEntity,
  ExperienceOfferEventContext,
  FrontPageEvent,
  SaveToTripBookingDetails,
} from './helpers'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { reportClientError } from 'services/errorReportingService'
import { GAImpressionFieldObject, GAProductFieldObject, SnowplowImpressionData } from 'analytics/eventDefinitions'
import { OptimizelyEvent } from 'analytics/optimizely/events'
import { getPageCategoryAndType } from './analyticsPageMapping'
import { OfferContextEvent } from './context'
import { optimizelyIsReady } from 'analytics/optimizely/optimizely'
import { allOptimizelyExperiments } from 'lib/optimizely/optimizelyUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import { getCartId } from 'selectors/checkoutSelectors'
import orderContext from './eventContexts/orderContext'
import paymentContext from './eventContexts/paymentContext'

export interface BaseSnowplowEvent {
  context?: Array<SelfDescribingJson> | null;
  // Only use this if you want to explicitly determine where the context handler looks in redux to determine the items data
  // By default is is determined by looking at what page the user is on or what information is on the page which currently works for all but one case
  itemContextSource?: 'order' | 'checkout' | 'offer'
}
export interface SelfDescribingSnowlplowEvent extends BaseSnowplowEvent {
  event: SelfDescribingJson
}
export interface PageviewSnowlplowEvent extends BaseSnowplowEvent {
  title: string;
  requiresItemContext: boolean;
}
export type SnowplowEvent = SelfDescribingSnowlplowEvent | PageviewSnowlplowEvent;

let _store: AppStore | undefined

export function registerCartTrackingStore(store: AppStore) {
  _store = store
}

/**
 * Event definitions
 */
export function impressionEvent(
  obj: GAImpressionFieldObject | GAProductFieldObject,
  listId: string,
  listName: string,
  currency: string,
  schema: string,
  lereVersion?: string,
): SnowplowEvent {
  const data: SnowplowImpressionData = {
    id: obj.id,
    name: obj.name,
    productType: obj.category,
    position: obj.position,
    currency,
    price: obj.price,
    listId,
    listName,
    listSource: lereVersion && `lere-${lereVersion}`,
  }
  return {
    event: {
      schema,
      data,
    },
  }
}

export function agentInsuranceUpselEvent(
  notSuitable: string,
  yes: string,
  no: string,
  whyNotSuitable?: string,
  isCovermore?: string,
  isOffered?: string,
  declineReason?: string,
): SnowplowEvent {
  return {
    context: buildInsuranceUpsellContext(
      notSuitable,
      yes,
      no,
      whyNotSuitable,
      isCovermore,
      isOffered,
      declineReason,
      _store ? _store.getState().auth.account.agentId : undefined,
    ),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'insurance_upsell_reminder',
      type: 'interaction',
      action: 'submit',
      category: 'checkout',
    }),
  }
}
export function cartEvent(eventType: CartTrackingEvent, extras: CartExtras = {}): SnowplowEvent | undefined {
  try {
    const { paymentType, errorReason } = extras
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const cartState: App.CartState = _store.getState().cart
    const eventData = cartEventsMap[eventType]
    return {
      context: [buildCartContext(cartState, paymentType, errorReason)],
      event: comluxgroup.createClientEvent_1_0_0({
        ...eventData,
      }),
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function experienceEvent(
  eventDefinitionKey: ExperienceEventDefinitionKey,
  geo: App.GeoState, eventsContexts?: Array<ExperienceEventEntity>,
): SnowplowEvent {
  const event = experienceOfferEventsMap[eventDefinitionKey]
  const eventsContextsWithGeo = eventsContexts?.map<ExperienceOfferEventContext>(eventContext => ({
    ...eventContext,
    data: {
      ...eventContext.data,
      geo,
    },
  })) ?? []
  return {
    context: eventsContextsWithGeo?.length ? getExperienceContext(eventsContextsWithGeo) : [],
    event: comluxgroup.createClientEvent_1_0_0({
      ...event,
      category: 'experiences_offer',
    }),
  }
}

export function clientEvent(
  subject: string,
  action: string,
  category: string,
  type: 'interaction' | 'nonInteraction' | 'operational',
): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      subject,
      action,
      type,
      category,
    }),
  }
}

export function uspAboutUsEvent(
  subject: string,
): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      subject,
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    }),
  }
}

export type AuthState = 'started' | 'succeeded' | 'failed'

export function logOperational(subject: string, action: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function logRegistration(provider: App.AuthSource, data: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: `${data}_succeeded`,
      subject: `${provider}_registration`,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function logAuth(provider: App.AuthSource, data: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: `${data}_succeeded`,
      subject: `${provider}_auth`,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function offerEvent(eventType: OfferContextEvent, offerPageContext: OfferPageState | undefined): SnowplowEvent | undefined {
  try {
    const ctx = getOfferContext(offerPageContext)
    if (ctx) {
      const event = offerEventsMap[eventType]

      return {
        context: [
          // ctx Re-enable after [REC-780]
        ],
        event: comluxgroup.createClientEvent_1_0_0({
          ...event,
          category: 'offer_page',
        }),
      }
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function offerPageHotLeadEvent(
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer | App.CruiseOffer,
  lead: App.HotLeadOffer,
  geo: App.GeoState,
): SnowplowEvent | undefined {
  try {
    const duration = lead.checkOut && lead.checkIn ? moment(lead.checkOut).diff(lead.checkIn, 'days') : undefined
    const contextItems = lead.occupancy.map(room => comluxgroup.createItem_1_0_0({
      brand: config.BRAND,
      currency: geo.currentCurrency,
      region: geo.currentRegionCode,
      offerId: offer.id,
      offerType: offer.type,
      travelStart: lead.checkIn,
      travelEnd: lead.checkOut,
      year: lead.year?.toString(),
      month: lead.month?.toString(),
      variant: lead.variant,
      duration,
      childrenAges: room.childrenAge,
      numberOfAdults: room.adults,
      numberOfChildren: room.children,
      numberOfInfants: room.infants,
    }))

    return {
      context: contextItems,
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'hot_lead',
        type: 'interaction',
        action: 'sent',
        category: 'offer_page',
      }),
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function frontPageClientEvent(eventType: FrontPageEvent, existingFilters?: App.OfferListFilters): SnowplowEvent | undefined {
  const uiSearchContext = comluxgroup.createSearch_1_0_0(buildSearchContext({
    searchBar: 'secondaryFilter',
    searchType: 'offerListFilters',
    searchParams: {
      filters: existingFilters ? mapToSnowplowFilters(existingFilters) : undefined,
    },
  }))
  return {
    context: [uiSearchContext],
    event: comluxgroup.createClientEvent_1_0_0(frontPageEventsMap[eventType]),
  }
}

export function searchEvent(subject: string, category: string, data: SearchEventData): SnowplowEvent {
  const { searchItem, checkIn, checkOut, vertical, isFlexibleDates, occupancies, filters } = data
  const baseContext = buildSearchContext()

  const formattedOccupancies = (occupancies ?? []).map(
    occupants => ({ numberOfChildren: (occupants?.infants ?? 0) + (occupants?.children ?? 0), numberOfAdults: occupants.adults }))

  return {
    context: [comluxgroup.createSearch_1_0_0({
      ...baseContext,
      searchType: searchItem?.searchType ?? '',
      verticals: [vertical],
      searchParams: {
        ...baseContext.searchParams,
        checkIn: checkIn ?? null,
        checkOut: checkOut ?? null,
        occupancies: formattedOccupancies,
        isFlexibleDates: Boolean(isFlexibleDates),
        searchText: searchItem?.format?.mainText ?? null,
        filters: filters ? mapToSnowplowFilters(filters) : null,
      },
    })],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'clicked',
      subject,
      type: 'interaction',
      category,
    }),
  }
}

function getConfirmationPageOrderId(routerState: RouterState): string | undefined {
  return routerState?.location?.query?.orderId
}

export function getItemContextMissingOfferState() {
  if (!_store) return
  const state: App.State = _store.getState()
  const pathParams = state.routeHistory.currentPathParams as { offerId?: string, id?: string }
  const offerId = pathParams.offerId || pathParams.id
  if (!offerId) return
  if (offerId) {
    return {
      offerError: state.offer.offerErrors[offerId],
      offerLoading: state.offer.offerSummariesLoading[offerId] && state.offer.offersLoading[offerId],
    }
  }
}

/**
 *
 * Use this function to add any global context to Snowplow events that are async
 * The function will be called right before each event is sent and awaited
 */
export async function asyncBaseContext(itemContextSource?: BaseSnowplowEvent['itemContextSource']): Promise<SelfDescribingJson<Record<string, unknown>>[] | null | undefined> {
  if (!_store) {
    return []
  }

  const state: App.State = _store.getState()

  const routerState: RouterState = state.router

  // See if the page is part of the mapped analytics pages
  const pageData = getPageCategoryAndType(routerState.location.pathname)

  // Otherwise, see if it is an offer page
  const pageCategory = await getPageOfferCategory(_store.getState())
  if (!pageData.category && !pageData.type && pageCategory) {
    pageData.category = pageCategory
    pageData.type = 'offer'
  }

  const context: SelfDescribingJson<Record<string, unknown>>[] = [
    comluxgroup.createPage_1_0_0({
      type: pageData.type,
      category: pageData.category,
    }),
  ]

  const promises: Promise<Array<SelfDescribingJson> | undefined>[] = []

  // We need to wait for optimizely to be ready before we can get the experiments context which is needed
  // for user context of snowplow
  promises.push(optimizelyIsReady.then((success) => {
    if (success) {
      const optimizelyExperimentData = allOptimizelyExperiments()
      return [comluxgroup.createExperimentContext_1_0_0(
        optimizelyExperimentData,
      )]
    }
    return []
  }))

  // Get items context just when it's an offer or checkout page
  const currentPath = state.routeHistory.currentPath
  const pathParams = state.routeHistory.currentPathParams as { orderId?: string, offerId?: string, id?: string, step?: string }
  const isCheckoutPage = pathParams.step && (currentPath.includes('checkout') || currentPath.includes('booking'))
  const isOfferPage = !!(pathParams.offerId || pathParams.id)
  const isOrderPage = !!(pathParams.orderId || getConfirmationPageOrderId(state.router))

  if (isCheckoutPage || itemContextSource === 'checkout') promises.push(getItemsContextForCheckout(state))
  else if (isOrderPage || itemContextSource === 'order') promises.push(getItemsContextForOrder(state))
  else if (isOfferPage || itemContextSource === 'offer') {
    const itemsContext = getItemsContextForOffer(state)
    if (itemsContext) context.push(...itemsContext)
  }
  const promiseContexts = await Promise.all(promises)
  context.push(...nonNullable(promiseContexts.flat()))
  return context
}

export function baseContext(): SelfDescribingJson[] {
  if (!_store) {
    return []
  }

  const state = _store.getState()

  const authState: App.AuthState = state.auth
  const geoState: App.GeoState = state.geo

  const context = [
    comluxgroup.createBrand_1_0_0({
      brand: config.BRAND,
    }),
    comluxgroup.createRegion_1_0_0({
      region: geoState.currentRegionCode,
    }),
  ]
  if (authState.account.memberId && authState.account.email) {
    setUserId(authState.account.memberId)
    context.push(comluxgroup.createUser_1_0_0({
      id: authState.account.memberId,
      email: authState.account.email,
      first_name: authState.account.givenName,
    }))
  }
  return context
}

export function signupSuccessEvent(method: App.SignUpAuthSource): SnowplowEvent {
  return {
    event: comluxgroup.createSignupSuccess_1_0_0({
      method,
    }),
  }
}

export function signinSuccessEvent(method: App.AuthSource): SnowplowEvent {
  return {
    event: comluxgroup.createSigninSuccess_1_0_0({
      method,
    }),
  }
}

export function addToTripDismissed(
  offer: App.AnyOffer,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  bookingDetails: SaveToTripBookingDetails = {},
): SnowplowEvent {
  return {
    context: createOfferItemContexts(offer, regionCode, currency, bookingDetails),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'dismissed_save_to_trip',
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function saveToTrip(
  offer: App.AnyOffer,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  bookingDetails: SaveToTripBookingDetails = {},
): SnowplowEvent & OptimizelyEvent {
  return {
    eventName: 'save-to-trip',
    type: 'event',
    context: createOfferItemContexts(offer, regionCode, currency, bookingDetails),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'pressed_save_to_trip',
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function selectPackageUpgradeOptionToCart(
  offer: Tours.V2Offer,
  option: string,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  tourItems: App.Checkout.TourV2Item[],
): SnowplowEvent {
  return {
    context: createTourOfferItemContexts(offer, regionCode, currency, tourItems),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: `tour_upgrade_${option.toLowerCase()}`,
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function tourTrendingDestination(country: string, action: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject: `tour_trending_destinations_${country}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function tourExploreCategory(category: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'pressed',
      subject: `tour_explore_category_${category}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function tourExploreOperator(operatorName: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'pressed',
      subject: `tour_explore_operator - ${operatorName}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function tourCarouselHomepage(category: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'view',
      subject: `offerlist-tourspage-${category}-carousel`,
      category: 'vertical_page',
      type: 'nonInteraction',
    }),
  }
}

export function clickViewAllCarouselExclusive(offerType: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'pressed',
      subject: `topexclusive-carousel-${offerType}-viewall`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function trackPageView(pathTemplate: string, pathParamsObject: any): SnowplowEvent {
  return {
    title: document.title,
    // temporary check, some events are missing item context, so we need to check if the event requires it and report to sentry
    requiresItemContext: window.location.href.includes('au/offer/'),
    context: [{
      schema: PAGE_VIEW_CONTEXT_SCHEMA,
      data: {
        pathTemplate,
        pathParams: JSON.stringify(pathParamsObject),
      },
    }],
  }
}

export function purchaseSuccess(order: App.Order): SnowplowEvent | undefined {
  try {
    const state = _store?.getState()
    if (!state) { throw new Error('No redux registered for tracking purchase success snowplow events') }
    const cartId = getCartId(state)
    return {
      // event is triggered right after transitioning to purchase success page but before loading order details in state
      // so it needs to specifically get it from the checkout state
      itemContextSource: 'checkout',
      context: [
        comluxgroup.createCart_1_0_0({
          cartId,
        }),
        orderContext(order),
        ...paymentContext(order.payments),
      ],
      event: comluxgroup.createPurchaseSuccess_1_0_0({}),
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function addToCart(): SnowplowEvent & OptimizelyEvent | undefined {
  try {
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const state = _store.getState()
    const cartId = getCartId(state)
    return {
      itemContextSource: 'checkout',
      type: 'event',
      eventName: 'add-to-cart',
      context: [{
        schema: CART_SCHEMA,
        data: {
          cartId,
        },
      }],
      event: comluxgroup.createAddToCart_1_0_0({}),
    }
  } catch (e) {
    reportClientError(e)
  }
}
